import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, PlusIcon, PencilIcon, TrashIcon, CheckSquareIcon } from 'lucide-react'
import { SERVICE_TYPE_OPTIONS } from '../lib/serviceTypeOptions'
import { useChecklistTemplates } from '../hooks/useChecklistTemplates'
import type { ChecklistTemplateItem } from '../lib/checklistTemplates'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileRowAction,
  MobileEmptyState,
  MobileFilterBar,
  MobileSelect,
  MobileStatStrip,
  MobileListGroup,
  MobileSection,
  MobileSearchField,
  mobile,
} from '../components/mobile'

export function ChecklistTemplates() {
  const navigate = useNavigate()
  const { templates, loading, refresh, saveTemplate, deleteTemplate } = useChecklistTemplates()
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [editor, setEditor] = useState<{
    isOpen: boolean
    id?: string
    name: string
    description: string
    service_type: string
    items: ChecklistTemplateItem[]
    saving: boolean
  } | null>(null)

  const openNew = () =>
    setEditor({
      isOpen: true,
      name: '',
      description: '',
      service_type: '',
      items: [{ label: '', sort_order: 0 }],
      saving: false,
    })

  const openEdit = (t: (typeof templates)[0]) =>
    setEditor({
      isOpen: true,
      id: t.id,
      name: t.name,
      description: t.description || '',
      service_type: t.service_type || '',
      items: t.items.length ? t.items.map((i) => ({ ...i })) : [{ label: '', sort_order: 0 }],
      saving: false,
    })

  const handleSave = async () => {
    if (!editor) return
    setEditor((prev) => (prev ? { ...prev, saving: true } : prev))
    const result = await saveTemplate({
      id: editor.id,
      name: editor.name,
      description: editor.description,
      service_type: editor.service_type || null,
      items: editor.items,
    })
    if (!result.ok) {
      setMessage({ type: 'error', text: result.error || 'Save failed' })
      setEditor((prev) => (prev ? { ...prev, saving: false } : prev))
      return
    }
    setMessage({ type: 'success', text: 'Template saved.' })
    setEditor(null)
    await refresh()
  }

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete template "${name}"?`)) return
    const result = await deleteTemplate(id)
    if (!result.ok) {
      setMessage({ type: 'error', text: result.error || 'Delete failed' })
      return
    }
    setMessage({ type: 'success', text: 'Template deleted.' })
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Checklist Templates"
        subtitle="Reusable job checklists by service type"
        onBack={() => navigate('/settings')}
        backLabel="Back to Settings"
        actions={
          <button type="button" onClick={openNew} className={mobile.primaryBtn}>
            <PlusIcon className="w-4 h-4" />
            <span className="sm:hidden">New</span>
            <span className="hidden sm:inline">New Template</span>
          </button>
        }
      />

        {message && (
          <div
            className={`p-3 rounded-xl border text-sm ${
              message.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="native-mobile-list space-y-2">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading templates...</div>
          ) : templates.length === 0 ? (
            <MobileEmptyState
              icon={<CheckSquareIcon className="w-12 h-12" />}
              title="No checklist templates yet"
              description="Create one to reuse across visit groups and jobs."
            />
          ) : (
            templates.map((t) => (
              <MobileListRow
                key={t.id}
                title={t.name}
                subtitle={
                  SERVICE_TYPE_OPTIONS.find((o) => o.value === t.service_type)?.label ||
                  t.service_type ||
                  'Any'
                }
                meta={
                  <div className="truncate">{t.items.map((i) => i.label).join(' · ') || '—'}</div>
                }
                footer={
                  <>
                    <MobileRowAction onClick={() => openEdit(t)}>
                      <PencilIcon className="w-4 h-4" /> Edit
                    </MobileRowAction>
                    <MobileRowAction tone="danger" onClick={() => handleDelete(t.id, t.name)}>
                      <TrashIcon className="w-4 h-4" /> Delete
                    </MobileRowAction>
                  </>
                }
                chevron={false}
              />
            ))
          )}
        </div>

        <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CheckSquareIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">No checklist templates yet</p>
              <p className="text-sm mt-1">Create one to reuse across visit groups and jobs.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Service type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {templates.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{t.name}</div>
                      {t.description && (
                        <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {SERVICE_TYPE_OPTIONS.find((o) => o.value === t.service_type)?.label ||
                        t.service_type ||
                        'Any'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {t.items.map((i) => i.label).join(' · ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openEdit(t)}
                        className="p-2 text-gray-500 hover:text-blue-600"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(t.id, t.name)}
                        className="p-2 text-gray-500 hover:text-red-600"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      {editor?.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editor.id ? 'Edit checklist template' : 'New checklist template'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  value={editor.name}
                  onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  value={editor.description}
                  onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service type</label>
                <select
                  value={editor.service_type}
                  onChange={(e) => setEditor({ ...editor, service_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Any</option>
                  {SERVICE_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Items</label>
                {editor.items.map((item, idx) => (
                  <div key={idx} className="flex flex-wrap gap-2 items-center">
                    <input
                      value={item.label}
                      onChange={(e) => {
                        const items = editor.items.map((it, i) =>
                          i === idx ? { ...it, label: e.target.value } : it
                        )
                        setEditor({ ...editor, items })
                      }}
                      className="flex-1 min-w-[10rem] px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Checklist item…"
                    />
                    <label className="inline-flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={!!item.photo_required}
                        onChange={(e) => {
                          const items = editor.items.map((it, i) =>
                            i === idx ? { ...it, photo_required: e.target.checked } : it
                          )
                          setEditor({ ...editor, items })
                        }}
                        className="rounded border-gray-300"
                      />
                      Photo required
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setEditor({
                          ...editor,
                          items: editor.items.filter((_, i) => i !== idx),
                        })
                      }
                      className="text-red-500"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setEditor({
                      ...editor,
                      items: [
                        ...editor.items,
                        {
                          label: '',
                          sort_order: editor.items.length,
                          photo_required: false,
                        },
                      ],
                    })
                  }
                  className="text-sm text-blue-600"
                >
                  + Add item
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                disabled={editor.saving}
                onClick={() => setEditor(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={editor.saving}
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {editor.saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
